import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { api } from '@/core/api/queries';
import { getErrorMessage, getErrorStatus, getFieldErrors } from '@/core/api/response-handler';
import { CONTACT_ECOTIC } from '@/constants/config';
import { EXEMPLU_TELEFON } from '@/shared/utils/telefon-national';
import {
    wasteContainerRequestsApi,
    type WasteContainerRequestDto,
    type WasteContainerRequestEditDto,
} from '../api/waste-container-requests.api';

/**
 * Mutatiile cererilor de recipiente (SP-04).
 *
 * Invalideaza INTREG routerul, nu doar `own`: o cerere noua schimba si ecranul
 * profesorului, si raportul de administrare, iar o marcare de pe raport schimba
 * si ce vede profesorul. O invalidare punctuala ar lasa un buton activ pe un fel
 * deja cerut — exact ecranul pe care S1 il interzice.
 *
 * 🔴 Fara toast de succes la `create`, INTENTIONAT: confirmarea e un REZUMAT pe
 * ecran, in aceeasi pozitie ca formularul (regula casetelor din design system —
 * chenar plin inainte, umplutura dupa). Un toast ar spune acelasi lucru de doua
 * ori si, pe 360, ar acoperi tocmai rezumatul.
 *
 * ⚠️ 409 («Școala a fost deja dotată») e o PLASA, nu fluxul: starea se citeste
 * inainte de apasare, si felul deja cerut n-are buton. Daca totusi apare — doua
 * taburi, doua sesiuni — reincarcarea lui `own` aduce ecranul la adevar. La 409
 * specific, SE-34 (owner, 2026-09-17): text propriu, cu telefonul si emailul de
 * contact — mesajul serverului e corect, dar utilizatorul ajuns aici printr-o
 * cursa nu are de unde sti ca poate scrie sau suna. Pe
 * `WasteContainerRequestService.comanda()`, 409 e SINGURA stare posibila cu
 * statusul asta pentru mutatia de creare (`CANTITATE_LIPSA` e 400), deci
 * statusul singur e suficient, fara sa potrivesc textul mesajului.
 */
export function mesajEroareCerere(error: unknown): string {
    if (getErrorStatus(error) === 409) {
        return `Pentru ediția aceasta, școala dumneavoastră are deja o comandă de recipiente de acest fel. Dacă mai aveți nevoie de recipiente sau ceva nu a ajuns, scrieți-ne la ${CONTACT_ECOTIC.email} sau sunați la ${CONTACT_ECOTIC.telefon}.`;
    }
    // Validarea pe campuri (400): titlul serverului («Eroare la validarea datelor. Verifică datele
    // trimise.») nu spune CARE camp — pe 07.10 un profesor a ramas blocat pe el. Spunem campul.
    const campuri = eroriCampuriCerere(error);
    const detalii = [
        campuri.numeContact && `Persoana de contact: ${campuri.numeContact}`,
        campuri.telefonContact && `Telefonul persoanei de contact: ${campuri.telefonContact}`,
        campuri.cantitate && `Câte minirecipiente: ${campuri.cantitate}`,
        campuri.adresa,
    ].filter(Boolean);
    if (detalii.length > 0) return `Comanda nu a putut fi trimisă. ${detalii.join(' ')}`;

    // Mesajul serverului, nu unul scris de noi: el stie de ce a refuzat
    // (cantitate lipsa, editie inchisa), si un text al nostru ar
    // imbatrani fix cand se schimba regula.
    return getErrorMessage(error, 'Comanda nu a putut fi trimisă');
}

/** Erorile serverului, asezate pe campurile formularului de comanda. */
export interface EroriCampuriCerere {
    numeContact?: string;
    telefonContact?: string;
    cantitate?: string;
    /** Adresa nu are input pe pagina: mesajul trimite la datele scolii. */
    adresa?: string;
}

export const MESAJ_ADRESA_INCOMPLETA =
    'Adresa școlii din contul dumneavoastră este incompletă (județ sau localitate). Completați-o din datele școlii, apoi comandați din nou.';

/**
 * Harta `errors` a backendului (`contactPersonPhone`, `deliveryAddress.judet`, …) tradusa pe
 * campurile formularului. Obiect gol cand eroarea nu are detalii pe campuri.
 */
export function eroriCampuriCerere(error: unknown): EroriCampuriCerere {
    const erori = getFieldErrors(error);
    if (!erori) return {};
    const rezultat: EroriCampuriCerere = {};
    if (erori.contactPersonName) rezultat.numeContact = erori.contactPersonName;
    // Mesajul serverului spune regula; modelul de numar corect il adaugam noi (owner, 07.10).
    if (erori.contactPersonPhone) {
        rezultat.telefonContact = `${erori.contactPersonPhone} Exemplu: ${EXEMPLU_TELEFON}.`;
    }
    if (erori.cantitate) rezultat.cantitate = erori.cantitate;
    if (Object.keys(erori).some((camp) => camp === 'deliveryAddress' || camp.startsWith('deliveryAddress.'))) {
        rezultat.adresa = MESAJ_ADRESA_INCOMPLETA;
    }
    return rezultat;
}

export function useRecipienteMutations() {
    const queryClient = useQueryClient();
    const invalidateAll = () =>
        queryClient.invalidateQueries({ queryKey: api.wasteContainerRequests.getKey() });

    const cere = useMutation({
        mutationFn: (dto: WasteContainerRequestDto) => wasteContainerRequestsApi.create(dto),
        onSuccess: invalidateAll,
        onError: (error) => {
            toast.error(mesajEroareCerere(error));
            invalidateAll();
        },
    });

    const marcheazaProcesata = useMutation({
        mutationFn: (id: string) => wasteContainerRequestsApi.markProcessed(id),
        onSuccess: invalidateAll,
        onError: (error) => toast.error(getErrorMessage(error, 'Cererea nu a putut fi marcată')),
    });

    // SE-18: PROCESATA -> LIVRATA.
    const marcheazaLivrata = useMutation({
        mutationFn: (id: string) => wasteContainerRequestsApi.markDelivered(id),
        onSuccess: invalidateAll,
        onError: (error) => toast.error(getErrorMessage(error, 'Cererea nu a putut fi marcată')),
    });

    // SE-18: corectii pe o cerere inca NOUA — 409 dupa ce a fost confirmata.
    const editeaza = useMutation({
        mutationFn: ({ id, dto }: { id: string; dto: WasteContainerRequestEditDto }) =>
            wasteContainerRequestsApi.edit(id, dto),
        onSuccess: invalidateAll,
        onError: (error) => toast.error(getErrorMessage(error, 'Cererea nu a putut fi salvată')),
    });

    return { cere, marcheazaProcesata, marcheazaLivrata, editeaza };
}
